import { debounce } from '@theme/theme';

/**
 * <hero-search>
 *
 * Live team lookup in the hero. The whole team list is rendered into the page
 * as JSON by the section, so matching happens in the browser: Shopify's
 * predictive-search endpoint only returns products, pages, collections and
 * articles, and the teams are a metaobject, so there is no server to ask.
 *
 * The debounce is short because nothing is fetched — it only stops the list
 * being rebuilt on every keystroke of a fast typist.
 */
const MAX_RESULTS = 6;
const INPUT_DEBOUNCE = 150;

if (!customElements.get('hero-search')) {
  customElements.define(
    'hero-search',
    class HeroSearch extends HTMLElement {
      constructor() {
        super();
        this.onInput = debounce(() => this.render(), INPUT_DEBOUNCE);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onSuggestionClick = this.onSuggestionClick.bind(this);
        this.activeIndex = -1;
        this.matches = [];
      }

      connectedCallback() {
        this.input = this.querySelector('[data-team-search-input]');
        this.panel = this.querySelector('[data-team-search-panel]');
        this.results = this.querySelector('[data-team-search-results]');
        this.empty = this.querySelector('[data-team-search-empty]');
        this.message = this.querySelector('[data-team-search-message]');

        if (!this.input || !this.panel) return;

        this.teams = this.readIndex();
        this.noResultsTemplate = this.dataset.noResults || '';

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown);
        this.panel.addEventListener('click', this.onSuggestionClick);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        if (!this.input) return;

        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeydown);
        this.panel.removeEventListener('click', this.onSuggestionClick);
        document.removeEventListener('click', this.onDocumentClick);
      }

      /**
       * A league chip is a plain link to the directory; the anchor does the
       * jump. This only announces which league was asked for, so
       * <team-directory> can select it before the visitor arrives. Delegated
       * from the panel because the chips are re-rendered as the query changes,
       * and `closest` because the click usually lands on the chip's icon.
       */
      onSuggestionClick(event) {
        const chip = event.target.closest('[data-team-search-league]');
        if (!chip) return;

        document.dispatchEvent(
          new CustomEvent('team-directory:filter', {
            detail: { league: chip.dataset.teamSearchLeague || '' },
          }),
        );

        this.close();
      }

      readIndex() {
        const script = this.querySelector('[data-team-search-index]');
        if (!script) return [];

        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed.filter((team) => team && team.name) : [];
        } catch (error) {
          console.error('hero-search: could not read the team index', error);
          return [];
        }
      }

      /**
       * Matches at word starts rather than anywhere in the string. The design
       * frame types "C" and returns Charlotte, Columbus, Corpus Christi,
       * Oklahoma City, Iowa Cubs and Sacramento River Cats — every hit is a
       * word beginning, and Rochester (a "c" mid-word) is absent.
       */
      static wordStarts(name, term) {
        const spans = [];
        const haystack = name.toLowerCase();
        let index = 0;

        while (index < haystack.length) {
          if (haystack.startsWith(term, index) && (index === 0 || /[\s/-]/.test(haystack[index - 1]))) {
            spans.push(index);
          }
          index += 1;
        }

        return spans;
      }

      render() {
        const term = this.input.value.trim().toLowerCase();

        if (!term) {
          this.close();
          return;
        }

        // The frame's example for "C" leads with Charlotte, Columbus and Corpus
        // Christi before Oklahoma City and Iowa Cubs, so a match on the first
        // word outranks one further into the name. Ties break alphabetically.
        this.matches = this.teams
          .map((team) => ({ team, spans: HeroSearch.wordStarts(team.name, term) }))
          .filter((entry) => entry.spans.length > 0)
          .sort((a, b) => {
            const rank = (a.spans[0] === 0 ? 0 : 1) - (b.spans[0] === 0 ? 0 : 1);
            return rank || a.team.name.localeCompare(b.team.name);
          })
          .slice(0, MAX_RESULTS);

        this.results.replaceChildren();
        this.activeIndex = -1;

        if (!this.matches.length) {
          this.showEmpty(this.input.value.trim());
          this.open();
          return;
        }

        this.empty.hidden = true;
        this.matches.forEach((entry, index) => {
          this.results.append(this.buildResult(entry, term, index));
        });

        // Nothing is preselected: the blue belongs to whatever the pointer or
        // the arrow keys land on, not to whichever team happened to sort first.
        this.setActive(-1);
        this.open();
      }

      buildResult({ team, spans }, term, index) {
        const item = document.createElement('li');
        item.className = 'hero-search__result';
        item.id = `${this.input.id}-option-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');

        // Same tab. Team URLs are this store's own collections written as full
        // https:// addresses, so testing for a scheme opened every result in a
        // new tab.
        const link = document.createElement('a');
        link.className = 'hero-search__result-link';
        link.href = team.url || '#';
        link.tabIndex = -1;

        const crest = document.createElement('span');
        crest.className = 'hero-search__result-crest';

        if (team.crest) {
          const image = document.createElement('img');
          image.src = team.crest;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 64;
          image.height = 64;
          crest.append(image);
        }

        const name = document.createElement('span');
        name.className = 'hero-search__result-name';
        name.append(...HeroSearch.highlight(team.name, spans, term.length));

        link.append(crest, name);
        item.append(link);
        return item;
      }

      /** Splits the name into plain text and <mark> runs at the matched spans. */
      static highlight(name, spans, length) {
        const nodes = [];
        let cursor = 0;

        spans.forEach((start) => {
          if (start < cursor) return;

          if (start > cursor) {
            nodes.push(document.createTextNode(name.slice(cursor, start)));
          }

          const mark = document.createElement('mark');
          mark.textContent = name.slice(start, start + length);
          nodes.push(mark);
          cursor = start + length;
        });

        if (cursor < name.length) {
          nodes.push(document.createTextNode(name.slice(cursor)));
        }

        return nodes;
      }

      showEmpty(terms) {
        this.empty.hidden = false;
        this.message.textContent = this.noResultsTemplate.replace('__TERMS__', terms);
      }

      get items() {
        return Array.from(this.results.children);
      }

      setActive(index) {
        this.items.forEach((item, position) => {
          const isActive = position === index;
          item.dataset.active = String(isActive);
          item.setAttribute('aria-selected', String(isActive));
        });

        this.activeIndex = index;

        const active = this.items[index];
        if (active) {
          this.input.setAttribute('aria-activedescendant', active.id);
        } else {
          this.input.removeAttribute('aria-activedescendant');
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
          return;
        }

        if (event.key === 'Enter') {
          // Nothing is highlighted until the visitor moves the cursor, so Enter
          // falls back to the top match — the behaviour that used to come free
          // from preselecting it.
          const index = this.activeIndex > -1 ? this.activeIndex : 0;
          const link = this.items[index]?.querySelector('a');
          if (link) {
            event.preventDefault();
            link.click();
          }
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        if (!this.items.length) return;

        event.preventDefault();

        // From "nothing highlighted", down opens at the first result and up at
        // the last. Stepping from -1 would otherwise land on the second-to-last.
        let next;
        if (this.activeIndex === -1) {
          next = event.key === 'ArrowDown' ? 0 : this.items.length - 1;
        } else {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          next = (this.activeIndex + step + this.items.length) % this.items.length;
        }

        this.setActive(next);
      }

      onDocumentClick(event) {
        if (this.panel.hidden || this.contains(event.target)) return;
        this.close();
      }

      open() {
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.panel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
      }
    },
  );
}
